import type { Country, Destination, Hotel, Resort } from '../model/types';
import type {
  ApiBool,
  ApiCountry,
  ApiCountryRef,
  ApiDestination,
  ApiHotel,
  ApiId,
  ApiResort,
} from './getOptions.types';

function toId(raw: ApiId | undefined): number {
  const n = typeof raw === 'string' ? Number(raw) : raw;
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

function toBool(raw: ApiBool | undefined): boolean {
  return !!raw;
}

function toNullableNumber(raw: number | null | undefined): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

function isCountryRef(raw: ApiId | ApiCountryRef | undefined): raw is ApiCountryRef {
  return typeof raw === 'object' && raw !== null;
}

function resolveCountryRef(
  country: ApiId | ApiCountryRef | undefined,
  countryName: string | undefined,
): { countryId: number; countryName: string } {
  if (isCountryRef(country)) {
    return {
      countryId: toId(country.id),
      countryName: country.name ?? countryName ?? '',
    };
  }
  return {
    countryId: toId(country),
    countryName: countryName ?? '',
  };
}

function adaptCountry(api: ApiCountry): Country {
  const visaDays = toNullableNumber(api.visaDays);
  const hasVisaDays = visaDays !== null && visaDays > 0;

  const country: Country = {
    id: toId(api.id),
    name: api.name,
    isPopular: toBool(api.isPopular),
    position: toNullableNumber(api.position),
    visaOnline: hasVisaDays && toBool(api.visaOnline),
    visaRequired: hasVisaDays && toBool(api.visaRequired),
    visaDays,
    minPrice: toNullableNumber(api.minPrice),
  };

  return country;
}

function adaptResort(api: ApiResort): Resort {
  const { countryId, countryName } = resolveCountryRef(api.country, api.countryName);
  return {
    id: toId(api.id),
    name: api.name,
    countryId,
    countryName,
    isPopular: toBool(api.isPopular),
    minPrice: toNullableNumber(api.minPrice),
  };
}

function adaptHotel(api: ApiHotel): Hotel {
  const { countryId, countryName } = resolveCountryRef(api.country, api.countryName);
  return {
    id: toId(api.id),
    name: api.name,
    countryId,
    countryName,
  };
}

export function adaptDestination(api: ApiDestination): Destination {
  switch (api.type) {
    case 'country':
      return { type: 'country', ...adaptCountry(api) };
    case 'resort':
      return { type: 'resort', ...adaptResort(api) };
    case 'hotel':
      return { type: 'hotel', ...adaptHotel(api) };
  }
}

export function adaptDestinations(list: ApiDestination[]): Destination[] {
  return list.map(adaptDestination);
}
